import { metaAI } from "neql"
import { useMemo } from "react"
import { AIModelAssignmentEnum } from "../Models/API/AIModel"
import { FetchState } from "../Models/Client/FetchState"
import { Result } from "../Models/Utility/Result"
import { useNeqlContextProvider } from "./useNeqlContextProvider"

/**
 * Returns status of AI model assignment for an app.
 * - `unused` means no AI model is assigned to the app for the given assignment type
 * - `active` means at least one AI model is up and running for the given assignment type
 * - `inactive` means one or more AI models are assigned to the app but none of them is up
 */
export function useAIModelAssignmentStatus(
    appId: string,
    assignmentType: AIModelAssignmentEnum
): FetchState<"unused" | "active" | "inactive"> {
    const client = useNeqlContextProvider("metaAIApp", appId)
    const { data: types, error } = metaAI.useGetAiModelAssignmentTypesQuery({
        client,
        variables: { appId }
    })

    return useMemo(() => {
        if (error !== undefined) {
            return Result.error(error.message)
        }

        if (types === undefined) {
            return undefined
        }

        return Result.ok(
            types.activeAssignments.some(a => a.assigned === assignmentType)
                ? "active"
                : types.assignments.some(a => a.assigned === assignmentType)
                ? "inactive"
                : "unused"
        )
    }, [assignmentType, error, types])
}
